"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { useQuery } from "@tanstack/react-query";

import { useTournamentReplayMetrics } from "@/entities/replay-metrics";
import type { GoodLuckCoreServicesTournamentPlayerStatDto } from "@/shared/api/generated-kubb/types/goodLuck/core/services/TournamentPlayerStatDto";
import { api } from "@/shared/lib/api";

import { PlayersTable, type PlayersTableRow } from "./players-table";

/**
 * KAN-877: контейнер таблицы игроков (эпик KAN-804).
 *
 * Соединяет ДВА ответа, потому что цифры живут в разных ручках: игры, W–L, KDA,
 * per-min и роль отдаёт `/player-stats`, а балл — `/replay-metrics`. Считать балл
 * здесь нельзя: его считает бэкенд (GL Score, KAN-764), и второй источник на клиенте
 * развёл бы одну и ту же оценку между секциями страницы.
 *
 * Соединение по `userId`. Игрок, который есть в метриках, но не в статистике турнира,
 * в таблицу не попадает: строка без игр и W–L была бы наполовину пустой, а метрики
 * реплеев могут содержать участников, которых в зачёте турнира нет.
 */

/**
 * KAN-873: тип ответа СГЕНЕРИРОВАН, рукописный удалён. Сверка при замене показала два
 * расхождения, и оба стоит знать.
 *
 * ПЕРВОЕ: рукописный тип объявлял восемь полей из двадцати одного. Ручка отдавала
 * `bestGame`, `avgCs`, `avgGold`, `avgDamage`, `avgVisionScore`,
 * `avgKillParticipationPercent`, `firstBloodRatePercent` — и об этом нельзя было
 * узнать, глядя во фронт. Ровно так завели кан с утверждением «в ручке нет урона и
 * золота в минуту»: поля были на месте, а рукописный тип о них молчал.
 *
 * ВТОРОЕ: спека СЛАБЕЕ контракта. В C# `UserId`, `DisplayName`, `AvgKda` и `Champions`
 * не-nullable, а генерация делает их необязательными — Swashbuckle не переносит
 * аннотации ссылочных типов в схему (это и есть предмет KAN-874). Поэтому строку
 * собирает адаптер ниже: он превращает слабый тип провода в строгий тип витрины и
 * ровно в одном месте отвечает за разрыв, вместо того чтобы разносить `?? ""` по
 * колонкам таблицы.
 */
type PlayerStatWire = GoodLuckCoreServicesTournamentPlayerStatDto;

async function fetchPlayerStats(tournamentId: string) {
  const res = await api.get<PlayerStatWire[]>(
    `/tournaments/${encodeURIComponent(tournamentId)}/player-stats`,
  );
  return res.data ?? [];
}

export function TournamentPlayersTableSection({
  tournamentId,
  scoreNoteByUserId,
  selectedUserId,
  onSelectPlayer,
}: {
  tournamentId: string;
  /** Почему балла нет — считает страница, ей видны метрики целиком. */
  scoreNoteByUserId?: Record<string, string | undefined>;
  selectedUserId?: string | null;
  onSelectPlayer?: (userId: string) => void;
}) {
  const tStats = useTranslations("TournamentStats");

  const { data: players = [] } = useQuery({
    // Ключ тот же, что у прежнего борда: пока обе витрины живут рядом, ответ
    // переиспользуется из кэша, а не запрашивается дважды.
    queryKey: ["tournaments", tournamentId, "player-stats"],
    queryFn: () => fetchPlayerStats(tournamentId),
    enabled: Boolean(tournamentId),
    staleTime: 30_000,
  });

  // Метрики реплеев есть не у каждого турнира. Их отсутствие — не ошибка таблицы:
  // колонка балла просто скроется, остальное покажется.
  const { data: metrics } = useTournamentReplayMetrics(tournamentId);

  const scoreByUserId = useMemo(() => {
    const map = new Map<string, number>();
    for (const player of metrics ?? []) {
      if (player.userId && typeof player.glScore === "number") {
        map.set(player.userId, player.glScore);
      }
    }
    return map;
  }, [metrics]);

  const rows = useMemo<PlayersTableRow[]>(
    () =>
      players.flatMap((player) => {
        // Строка БЕЗ `userId` пропускается целиком, а не достраивается пустышкой:
        // по нему таблица выбирает игрока и берёт балл, и строка, которую нельзя
        // выбрать, читалась бы как игрок, у которого «ничего нет».
        if (!player.userId) return [];

        return [
          {
            userId: player.userId,
            // Дальше — поля, которые контракт C# гарантирует, а схема объявила
            // необязательными (KAN-874). Подстановка здесь недостижима, пока
            // контракт не изменится; если изменится — увидим пустое имя и прочерк
            // в счёте, а не сломанную таблицу.
            displayName: player.displayName ?? "",
            avatarUrl: player.avatarUrl,
            teamName: player.teamName,
            role: player.role,
            games: player.games ?? 0,
            wins: player.wins ?? 0,
            losses: player.losses ?? 0,
            winrate: player.winrate ?? 0,
            avgKda: player.avgKda ?? "—",
            kdaRatio: player.kdaRatio ?? 0,
            // А эти в C# действительно nullable: LCU заполняет не всё. Ноль вместо
            // null сделал бы «не измеряли» неотличимым от «ноль за игру» (KAN-724).
            damagePerMin: player.avgDamagePerMin,
            goldPerMin: player.avgGoldPerMin,
            csPerMin: player.avgCsPerMin,
            champions: (player.champions ?? []).flatMap((c) =>
              c.championName ? [{ championName: c.championName, games: c.games ?? 0 }] : [],
            ),
            score: scoreByUserId.get(player.userId) ?? null,
            // Игрок, которого нет в метриках ВООБЩЕ, причины не получает от страницы —
            // её там неоткуда взять. Для него это «игры не попали в разбор».
            scoreNote:
              scoreNoteByUserId?.[player.userId] ?? tStats("scoreNote.noReplays"),
          },
        ];
      }),
    [players, scoreByUserId, scoreNoteByUserId, tStats],
  );

  return (
    <PlayersTable
      rows={rows}
      selectedUserId={selectedUserId}
      onSelectPlayer={onSelectPlayer}
    />
  );
}
